import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import './DeleteConfirmPopover.css';

/**
 * DeleteConfirmPopover
 * Lightweight non-modal confirmation popover anchored to a clicked icon/button.
 *
 * Props:
 *   pendingDelete   — { anchor: HTMLElement, ...callerData } | null
 *                     When non-null, the popover renders, measures itself, and
 *                     positions against `anchor` using fixed coords so it
 *                     escapes any overflow:auto ancestor.
 *   onDismiss       — () => void   Cancel, Escape, outside click, (optional) scroll
 *   onConfirm       — () => void   Confirm button
 *   message         — string       Body copy
 *   confirmLabel    — string       Confirm button text. Default 'Delete'.
 *   confirmVariant  — 'danger' | 'primary'  Confirm button color. Default 'danger'
 *                     (red, matches the destructive entry-delete usage).
 *   dismissOnScroll — boolean      Close on scroll. Default true. Useful when
 *                     the anchor lives in a scrollable container where drift
 *                     would look broken. Set false when the popover is
 *                     anchored to something on a static page.
 */
const MESSAGE_ID = 'delete-confirm-message';

const DeleteConfirmPopover = ({
  pendingDelete,
  onDismiss,
  onConfirm,
  message,
  confirmLabel = 'Delete',
  confirmVariant = 'danger',
  dismissOnScroll = true,
}) => {
  const popoverRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, ready: false });

  // Reset readiness when the anchor changes so we re-measure before showing.
  useLayoutEffect(() => {
    setPosition((prev) => ({ ...prev, ready: false }));
  }, [pendingDelete]);

  // Measure and position once the popover is in the DOM.
  useLayoutEffect(() => {
    if (!pendingDelete || !popoverRef.current) return;
    const anchor = pendingDelete.anchor;
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popoverRef.current.getBoundingClientRect();
    const margin = 8;
    const viewportPad = 8;
    const popoverHeight = popoverRect.height;
    const popoverWidth = popoverRect.width;
    const spaceBelow = window.innerHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;

    let top;
    if (spaceBelow >= popoverHeight + margin + viewportPad) {
      top = anchorRect.bottom + margin;
    } else if (spaceAbove >= popoverHeight + margin + viewportPad) {
      top = anchorRect.top - popoverHeight - margin;
    } else {
      // Neither direction has enough room — clamp inside viewport.
      top = Math.max(
        viewportPad,
        Math.min(anchorRect.bottom + margin, window.innerHeight - popoverHeight - viewportPad)
      );
    }

    // Horizontally align the popover's right edge with the anchor's right edge,
    // then clamp to viewport.
    let left = anchorRect.right - popoverWidth;
    left = Math.max(
      viewportPad,
      Math.min(left, window.innerWidth - popoverWidth - viewportPad)
    );

    setPosition({ top, left, ready: true });
  }, [pendingDelete]);

  // Focus management, keyboard dismissal, outside-click dismissal, scroll dismissal.
  useEffect(() => {
    if (!pendingDelete) return undefined;
    const anchor = pendingDelete.anchor;

    // Move focus to the Cancel button on open (safer default than Confirm).
    const focusTimer = window.setTimeout(() => {
      cancelButtonRef.current?.focus();
    }, 0);

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onDismiss();
      }
    };
    const handleMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onDismiss();
      }
    };
    const handleScroll = () => onDismiss();

    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleMouseDown);
    if (dismissOnScroll) {
      document.addEventListener('scroll', handleScroll, true); // capture — catches scroll containers
    }

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleMouseDown);
      if (dismissOnScroll) {
        document.removeEventListener('scroll', handleScroll, true);
      }
      // Restore focus to the originating anchor.
      if (anchor && typeof anchor.focus === 'function') {
        anchor.focus();
      }
    };
  }, [pendingDelete, onDismiss, dismissOnScroll]);

  if (!pendingDelete) return null;

  return (
    <div
      ref={popoverRef}
      className="delete-confirm-popover"
      role="dialog"
      aria-modal="false"
      aria-labelledby={MESSAGE_ID}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        visibility: position.ready ? 'visible' : 'hidden',
      }}
    >
      <p id={MESSAGE_ID} className="delete-confirm-message">
        {message}
      </p>
      <div className="delete-confirm-actions">
        <button
          type="button"
          ref={cancelButtonRef}
          className="delete-confirm-cancel"
          onClick={onDismiss}
        >
          Cancel
        </button>
        <button
          type="button"
          className={`delete-confirm-confirm delete-confirm-confirm--${confirmVariant}`}
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  );
};

export default DeleteConfirmPopover;
